import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import AiText from "@/components/AiText";
import { Clock, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Trophy, ArrowLeft } from "lucide-react";
import QuestionText from "@/components/QuestionText";

const BE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (u ? `${BE}${u}` : null);

export default function ExamTake() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [current, setCurrent] = useState(0);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/exams/${id}`).then(({ data }) => {
      setExam(data);
      setTimeLeft(data.duration_min * 60);
    }).catch((err) => {
      if (err.response?.status === 402) { toast.error("Premium abonelik gerekli"); navigate("/abonelik"); }
      else navigate("/denemeler");
    });
  }, [id, navigate]);

  const submit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/exams/${id}/submit`, { answers });
      setResult(data);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Sınav gönderilemedi");
    } finally {
      setSubmitting(false);
    }
  }, [answers, id, submitting]);

  useEffect(() => {
    if (!exam || result || timeLeft <= 0) return;
    const t = setInterval(() => setTimeLeft((s) => {
      if (s <= 1) { clearInterval(t); submit(); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [exam, result, timeLeft, submit]);

  if (!exam) return <div className="text-center py-20 text-4xl animate-bounce">🦉</div>;

  if (result) {
    const great = result.score >= 70;
    return (
      <div className="max-w-2xl mx-auto space-y-6 no-copy" onContextMenu={(e) => e.preventDefault()} onCopy={(e) => e.preventDefault()}>
        <div className={`rounded-3xl p-8 text-center text-white ${great ? "bg-emerald-500" : "bg-amber-500"} animate-pop-in`} data-testid="exam-result">
          <Trophy className="w-14 h-14 mx-auto" />
          <h1 className="font-display text-3xl font-bold mt-3">%{result.score}</h1>
          <p className="mt-1 font-semibold">{result.correct} / {result.total} doğru</p>
          <p className="mt-2 text-white/90">{great ? "Harikasın! 🎉" : "Güzel deneme! Yanlışlarına bakalım 💪"}</p>
        </div>

        {result.wrong.length > 0 && (
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 mb-3">Yanlış Yaptığın Sorular</h2>
            <div className="space-y-4">
              {result.wrong.map((r, i) => (
                <div key={r.question_id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`wrong-question-${i}`}>
                  <p className="text-xs font-bold text-rose-500 uppercase">{r.subject}</p>
                  <p className="font-semibold text-slate-900 mt-1"><QuestionText text={r.text} /></p>
                  {r.image_url && <img src={imgSrc(r.image_url)} alt="Soru görseli" className="mt-2 rounded-xl border border-slate-200 max-w-full" />}
                  <div className="mt-3 space-y-2">
                    {r.options.map((opt, oi) => (
                      <div key={oi} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-sm ${oi === r.correct_index ? "bg-emerald-50 text-emerald-700 font-semibold" : oi === r.selected_index ? "bg-rose-50 text-rose-700" : "text-slate-500"}`}>
                        {oi === r.correct_index ? <CheckCircle2 className="w-4 h-4" /> : oi === r.selected_index ? <XCircle className="w-4 h-4" /> : <span className="w-4" />}
                        {opt}
                      </div>
                    ))}
                  </div>
                  {r.explanation && (
                    <div className="mt-3 bg-indigo-50 rounded-xl p-4 flex gap-2">
                      <span className="text-xl">🦉</span>
                      <div><p className="text-xs font-bold text-indigo-600 mb-1">Nokta Öğretmen açıklıyor:</p><AiText text={r.explanation} /></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button data-testid="back-to-exams-btn" onClick={() => navigate("/denemeler")} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl font-bold flex-1 py-6">
            Denemelere Dön
          </Button>
        </div>
      </div>
    );
  }

  const q = exam.questions[current];
  const mins = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const secs = String(timeLeft % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  return (
    <div className="max-w-2xl mx-auto space-y-5 no-copy" onContextMenu={(e) => e.preventDefault()} onCopy={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between">
        <button onClick={() => navigate("/denemeler")} className="flex items-center gap-1 text-sm text-slate-500 font-semibold"><ArrowLeft className="w-4 h-4" /> Çık</button>
        <div className={`flex items-center gap-1.5 font-bold px-3 py-1.5 rounded-full text-sm ${timeLeft < 60 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700"}`} data-testid="exam-timer">
          <Clock className="w-4 h-4" /> {mins}:{secs}
        </div>
      </div>

      <div className="bg-white rounded-full h-2.5 overflow-hidden">
        <div className="bg-indigo-500 h-full transition-all" style={{ width: `${((current + 1) / exam.questions.length) * 100}%` }} />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-bold text-indigo-600 uppercase">{q.subject}</p>
          <p className="text-xs text-slate-400 font-semibold">Soru {current + 1} / {exam.questions.length}</p>
        </div>
        <p className="font-semibold text-slate-900 text-lg" data-testid="question-text"><QuestionText text={q.text} /></p>
        {q.image_url && <img src={imgSrc(q.image_url)} alt="Soru görseli" data-testid="question-image" className="mt-3 rounded-xl border border-slate-200 max-w-full" />}
        <div className="mt-5 space-y-3">
          {q.options.map((opt, oi) => (
            <button key={oi} data-testid={`option-${oi}`} onClick={() => setAnswers({ ...answers, [q.id]: oi })}
              className={`w-full text-left rounded-xl px-4 py-3.5 border-2 font-medium transition ${answers[q.id] === oi ? "border-indigo-500 bg-indigo-50 text-indigo-800" : "border-slate-200 text-slate-700 hover:border-indigo-200"}`}>
              <span className="font-bold mr-2">{String.fromCharCode(65 + oi)})</span>{opt}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <Button data-testid="prev-btn" variant="outline" disabled={current === 0} onClick={() => setCurrent(current - 1)} className="rounded-xl font-bold border-2">
          <ChevronLeft className="w-4 h-4" /> Önceki
        </Button>
        <span className="text-xs text-slate-400 font-semibold">{answered}/{exam.questions.length} işaretlendi</span>
        {current < exam.questions.length - 1 ? (
          <Button data-testid="next-btn" onClick={() => setCurrent(current + 1)} className="bg-indigo-600 hover:bg-indigo-700 rounded-xl font-bold">
            Sonraki <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button data-testid="submit-exam-btn" onClick={submit} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600 rounded-xl font-bold">
            Sınavı Bitir
          </Button>
        )}
      </div>
    </div>
  );
}
